import type { FunctionComponent } from "react";
import type {
  ActionOutputData,
  InputData,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import { ActivityDetailsTriggerGroup } from "../ActivityDetailsTriggerGroup/ActivityDetailsTriggerGroup";
import { ActivityDetailsItem } from "../ActivityDetailsItem/ActivityDetailsItem";
import {
  OUTPUT_APPLICATION_ID,
  RECORD_ID,
} from "../../../../../utils/constants";
import {
  addEmptyValueIfNeeded,
  findRecordValue,
  getInputLabel,
} from "../../../ActivityGrid/ActivityGrid.helpers";
import { getInputValue as defaultGetInputValue } from "../GeneralActionResultGroup/utils";
import type { AutomationsStore } from "../../../../../stores/automation/index.store";
import type { ActionCandidate } from "../../../../../types/candidate";
import type { JobDetails } from "../ActivityDetails.types";

export const GeneralActionResultGroupWrapper: FunctionComponent<{
  index: number;
  jobDetails: JobDetails;
  onDetailsClickWithFetch?: (recordJodId: string) => Promise<void>;
  onDetailsClick?: () => void;
  recordJodId?: string;
  action: ActionOutputData;
  automations: AutomationsStore;
  selectedActionFallback: ActionCandidate | undefined;
  recordJob?: RecordJobData;
  getInputValue?: (
    input: InputData,
    jobDetails: JobDetails,
    recordJob?: RecordJobData,
  ) => string;
  shouldDisplayInput?: (input: InputData, recordJob?: RecordJobData) => boolean;
}> = ({
  index,
  jobDetails,
  recordJob,
  onDetailsClickWithFetch,
  onDetailsClick,
  recordJodId,
  action,
  automations,
  selectedActionFallback,
  getInputValue = defaultGetInputValue,
  shouldDisplayInput = () => true,
}) => {
  const record =
    recordJob?.outputData?.recordList?.recordList !== undefined
      ? recordJob.outputData.recordList.recordList.records[0]
      : recordJob?.record;

  const output = record?.outputs.find(
    (output) => output.outputId === RECORD_ID,
  );

  return (
    <ActivityDetailsTriggerGroup
      key={`Record ${index + 1}`}
      title={`Record ${index + 1}`}
      onClick={() => {
        if (
          onDetailsClickWithFetch !== undefined &&
          recordJodId !== undefined
        ) {
          void onDetailsClickWithFetch(recordJodId);
        }
        if (onDetailsClick !== undefined) {
          onDetailsClick();
        }
      }}
    >
      {recordJob !== undefined ? (
        <>
          {recordJob.inputs
            .filter(
              (input) =>
                ![RECORD_ID, OUTPUT_APPLICATION_ID].includes(input.inputId) &&
                shouldDisplayInput(input, recordJob),
            )
            .filter((input) => {
              if (selectedActionFallback?.description.inputs !== undefined) {
                const inputDescription =
                  selectedActionFallback.description.inputs.find(
                    (inputDesc) => inputDesc.inputId === input.inputId,
                  );
                if (inputDescription !== undefined) {
                  return !(
                    inputDescription.editor?.editor?.oneofKind === "hidden" &&
                    getInputValue(input, jobDetails, recordJob).trim() === ""
                  );
                }
              }
              return true;
            })
            .flatMap((input) => {
              return (
                <ActivityDetailsItem
                  key={input.inputId}
                  label={getInputLabel(
                    input,
                    jobDetails,
                    action,
                    automations.candidate.allActions,
                    selectedActionFallback,
                    index,
                  )}
                >
                  {addEmptyValueIfNeeded(
                    getInputValue(input, jobDetails, recordJob),
                  )}
                </ActivityDetailsItem>
              );
            })}
          {output !== undefined && (
            <ActivityDetailsItem label={output.outputLabel}>
              {addEmptyValueIfNeeded(findRecordValue(output.outputId, record))}
            </ActivityDetailsItem>
          )}
        </>
      ) : null}
    </ActivityDetailsTriggerGroup>
  );
};
